import { useCallback, useEffect, useRef, useState } from 'react';
import Hammer from 'hammerjs';
import useWindowDimensions from './useWindowDimensions';

export default function useOverlayVideo({
	triggerRef,
	videoRef,
	videoLeftRef,
	videoRightRef,
	videoAspectRatio = 0.5625
}) {
	const requestIdRef = useRef();
	const closeTimeoutRef = useRef();
	const { vh, vw } = useWindowDimensions();

	const [opened, setOpened] = useState(false);
	const [progressVal, setProgressVal] = useState('0');
	const [left, setLeft] = useState(0);
	const [top, setTop] = useState(0);
	const [isMuted, setIsMuted] = useState(true);
	const [containerWidth, setContainerWidth] = useState('100%');
	const [containerMarginLeft, setContainerMarginLeft] = useState(0);

	const getCoords = useCallback((element) => {
		if (!element) {
			return { top: 0, left: 0 };
		}

		const rect = element.getBoundingClientRect();
		return { top: rect.top, left: rect.left };
	}, []);

	const getContainerWidth = useCallback(() => {
		if (vw / vh > videoAspectRatio && vw > 420) {
			return videoAspectRatio * vh;
		}

		return '100%';
	}, [vh, vw, videoAspectRatio]);

	const positionContainer = useCallback(() => {
		if (vw / vh > videoAspectRatio && vw > 420) {
			setContainerWidth(getContainerWidth());
			setLeft('50%');
			setContainerMarginLeft(-(videoAspectRatio * vh / 2));
			return;
		}

		setContainerWidth('100%');
		setLeft(0);
		setContainerMarginLeft(0);
	}, [getContainerWidth, vh, vw, videoAspectRatio]);

	const updateProgress = useCallback((video) => {
		if (!video || !video.duration) {
			return;
		}

		setProgressVal(Math.round((video.currentTime / video.duration) * 100));
	}, []);

	const stop = useCallback((video) => {
		if (!video) {
			return;
		}

		if (requestIdRef.current) {
			video.pause();
			window.cancelAnimationFrame(requestIdRef.current);
			requestIdRef.current = undefined;
		}
	}, []);

	const reset = useCallback((video) => {
		if (!video) {
			return;
		}

		video.currentTime = 0;
	}, []);

	const closeOverlay = useCallback(() => {
		const trigger = triggerRef.current;
		const video = videoRef.current;

		stop(video);
		setLeft(getCoords(trigger).left);
		setContainerMarginLeft(0);
		document.body.classList.remove('overlay-animation');
		document.body.classList.remove('overlay-lock');
		setOpened(false);
		setIsMuted(true);

		if (closeTimeoutRef.current) {
			window.clearTimeout(closeTimeoutRef.current);
		}

		closeTimeoutRef.current = window.setTimeout(() => {
			document.body.classList.remove('overlay-open');
			if (video) {
				video.muted = true;
			}
			reset(video);
			updateProgress(video);
		}, 300);
	}, [getCoords, reset, stop, triggerRef, updateProgress, videoRef]);

	const start = useCallback((video) => {
		if (!video) {
			return;
		}

		if (!requestIdRef.current && video.currentTime !== video.duration) {
			video.play();
			requestIdRef.current = window.requestAnimationFrame(() => {
				requestIdRef.current = undefined;
				updateProgress(video);
				start(video);
			});
			return;
		}

		closeOverlay();
	}, [closeOverlay, updateProgress]);

	const openOverlay = useCallback(() => {
		const trigger = triggerRef.current;
		const video = videoRef.current;

		if (!video) {
			return;
		}

		setOpened(true);
		setIsMuted(true);
		video.muted = true;
		setLeft(getCoords(trigger).left);
		setTop(getCoords(trigger).top);
		positionContainer();
		document.body.classList.add('overlay-open');
		document.body.classList.add('overlay-lock');
		window.setTimeout(() => {
			document.body.classList.add('overlay-animation');
		}, 1);
		window.setTimeout(() => {
			start(video);
		}, 1);
	}, [getCoords, positionContainer, start, triggerRef, videoRef]);

	const toggleSound = useCallback(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}

		video.muted = !video.muted;
		setIsMuted(video.muted);
	}, [videoRef]);

	useEffect(() => {
		setContainerWidth(getContainerWidth());
		const trigger = triggerRef.current;
		setLeft(getCoords(trigger).left);
		setTop(getCoords(trigger).top);
	}, [getContainerWidth, getCoords, triggerRef, vh, vw]);

	useEffect(() => {
		const leftNode = videoLeftRef.current;
		const rightNode = videoRightRef.current;
		if (!leftNode || !rightNode) {
			return;
		}

		const leftHammer = new Hammer(leftNode);
		const rightHammer = new Hammer(rightNode);
		leftHammer.get('swipe').set({ direction: Hammer.DIRECTION_DOWN });
		leftHammer.get('tap').set();
		rightHammer.get('swipe').set({ direction: Hammer.DIRECTION_DOWN });
		rightHammer.get('tap').set();

		const handleLeftSwipe = () => closeOverlay();
		const handleLeftTap = () => reset(videoRef.current);
		const handleRightGesture = () => closeOverlay();

		leftHammer.on('swipe', handleLeftSwipe);
		leftHammer.on('tap', handleLeftTap);
		rightHammer.on('swipe tap', handleRightGesture);

		return () => {
			leftHammer.off('swipe', handleLeftSwipe);
			leftHammer.off('tap', handleLeftTap);
			rightHammer.off('swipe tap', handleRightGesture);
			leftHammer.destroy();
			rightHammer.destroy();
		};
	}, [closeOverlay, reset, videoLeftRef, videoRef, videoRightRef]);

	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				window.clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	return {
		opened,
		progressVal,
		left,
		top,
		isMuted,
		containerWidth,
		containerMarginLeft,
		openOverlay,
		closeOverlay,
		toggleSound
	};
}
